/* eslint-disable camelcase */
import { services } from '~/config/constants';
import API from './api';

const { coreService } = services;

/**
 * Note there is no application_id here: the axios instance's baseURL is
 * `${API_HOST}/${API_ID}`, and the gateway strips `/core-service` from the
 * middle, so `/{API_ID}/core-service/{userId}/admin/...` reaches Core as
 * `/<application_id>/<user_id>/admin/...`. Adding it here would produce a
 * doubled segment and a 404.
 *
 * @param {String} userId member id
 * @returns {String} the admin endpoint prefix
 */
const adminBase = (userId: string) => `${coreService}/${userId}/admin`;

/**
 * Eligibility, admin session state, and — when a live session matches the
 * binding tuple — a freshly minted session token.
 *
 * Called on every authenticated page load, not only on entry to Admin: the
 * expiry warning has to reach a member who has left the Admin area, and it
 * cannot count down to a deadline it was never told about.
 *
 * skipCache is required. The RequestCache holds GET responses for a second,
 * and this is polled as the deadline approaches and again at zero.
 *
 * @param {String} userId member id
 * @returns {Promise<any>} eligibility, session state and possibly a token
 */
export const getAdminAccess = async (userId: string) => {
    return await API({
        endpoint: `${adminBase(userId)}/access`,
        method: 'GET',
        skipCache: true
    });
};

/**
 * Send or resend the code — one endpoint for both.
 *
 * `resend` is the only thing that counts against the resend limit. Called
 * without it when a code is already outstanding, the server reports that
 * challenge and sends nothing, so re-entering the page or reloading it neither
 * spends a resend nor trips the cooldown.
 *
 * `area` names what the code is for, and only the email reads it: Core serves
 * this one endpoint to both front ends, so it cannot tell the welfare app from
 * the admin app and the mail would otherwise say "Admin area" to a member
 * signing in here. Core matches it against a table of phrases and never
 * interpolates it; an unknown value falls back to the admin wording.
 *
 * @param {String} userId member id
 * @param {Boolean} [isResend] true only from the Resend affordance
 * @returns {Promise<any>} masked destination and cooldown deadlines
 */
export const requestAdminMfaChallenge = async (userId: string, isResend = false) => {
    return await API({
        endpoint: `${adminBase(userId)}/mfa/challenge`,
        method: 'POST',
        data: { resend: isResend, area: 'welfare' }
    });
};

export const verifyAdminMfaCode = async (userId: string, code: string) => {
    return await API({
        endpoint: `${adminBase(userId)}/mfa/verify`,
        method: 'POST',
        data: { code }
    });
};

/**
 * Slide the session forward. Called on an admin route *change* and by the
 * expiry warning's button — never on mount, and never on a timer.
 *
 * The server no-ops above its 30-minute threshold, so this is safe to call on
 * navigation without inflating a session that was in no danger. Scaffolding for
 * as long as enforcement is deferred: when a decorator starts sliding the
 * session on admin requests, this and its hook go away together.
 *
 * @param {String} userId member id
 * @returns {Promise<any>} the session, slid or unchanged
 */
export const refreshAdminMfaSession = async (userId: string) => {
    return await API({
        endpoint: `${adminBase(userId)}/mfa/session/refresh`,
        method: 'POST',
        data: {}
    });
};

/**
 * End admin mode — sign-out, and "Leave Admin" on the expiry warning.
 *
 * @param {String} userId member id
 * @returns {Promise<any>} an empty session state
 */
export const revokeAdminMfaSession = async (userId: string) => {
    return await API({
        endpoint: `${adminBase(userId)}/mfa/session`,
        method: 'DELETE'
    });
};
